export type ResultsView = "overview" | "pools" | "bracket" | "player";
export type BracketDisplayMode = "rounds" | "tree";
/** `auto` = pas de param `view` → priorité sportive joueur (poules puis tableau). */
export type ResultsViewParam = ResultsView | "auto";

export interface ResultsNavState {
  view: ResultsViewParam;
  poule: string | null;
  joueur: string | null;
  tour: string | null;
  mode: BracketDisplayMode;
}

export const RESULTS_VIEWS: ResultsView[] = [
  "overview",
  "pools",
  "bracket",
  "player",
];

const VIEW_SET = new Set<string>(RESULTS_VIEWS);

export function parseResultsView(raw: string | null): ResultsViewParam {
  if (!raw) return "auto";
  if (VIEW_SET.has(raw)) return raw as ResultsView;
  return "auto";
}

export function parseBracketMode(raw: string | null): BracketDisplayMode {
  return raw === "tree" ? "tree" : "rounds";
}

export function parseResultsNav(
  params: URLSearchParams
): ResultsNavState {
  return {
    view: parseResultsView(params.get("view")),
    poule: params.get("poule"),
    joueur: params.get("joueur"),
    tour: params.get("tour"),
    mode: parseBracketMode(params.get("mode")),
  };
}

/**
 * Priorité joueur : tirage/classements des poules, sinon tableau final, sinon aperçu.
 */
export function resolveResultsView(
  view: ResultsViewParam,
  doc: { pools: { length: number }; rounds: { length: number } }
): ResultsView {
  if (view !== "auto") return view;
  if (doc.pools.length > 0) return "pools";
  if (doc.rounds.length > 0) return "bracket";
  return "overview";
}

export type ResultsNavPatch = Partial<{
  view: ResultsView | "auto" | null;
  poule: string | null;
  joueur: string | null;
  tour: string | null;
  mode: BracketDisplayMode | null;
  tableau: string | null;
}>;

/**
 * Applique un patch aux search params résultats.
 * `null` retire la clé ; `undefined` la laisse inchangée.
 * `view: "auto"` retire le param pour laisser le défaut sportif.
 */
export function applyResultsNavPatch(
  current: URLSearchParams,
  patch: ResultsNavPatch
): URLSearchParams {
  const next = new URLSearchParams(current);

  const setOrDelete = (key: string, value: string | null | undefined): void => {
    if (value === undefined) return;
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
  };

  if (patch.view !== undefined) {
    if (patch.view === null || patch.view === "auto") next.delete("view");
    else next.set("view", patch.view);
  }
  setOrDelete("poule", patch.poule);
  setOrDelete("joueur", patch.joueur);
  setOrDelete("tour", patch.tour);
  setOrDelete("mode", patch.mode === null ? null : patch.mode);
  setOrDelete("tableau", patch.tableau);

  if (next.get("mode") === "rounds") next.delete("mode");

  return next;
}

export function poolProgress(pool: {
  matches: { status: string }[];
}): { completed: number; total: number } | null {
  const total = pool.matches.length;
  if (total === 0) return null;
  const completed = pool.matches.filter((m) =>
    ["TERMINE", "EXEMPT", "FORFAIT", "ABANDON"].includes(m.status)
  ).length;
  return { completed, total };
}

export function countQualified(
  pool: { standings: { qualification?: string }[] }
): number {
  return pool.standings.filter((s) => s.qualification === "QUALIFIED").length;
}

/**
 * Choisit le tableau à afficher : filtre URL valide, sinon unique tableau
 * contenant le joueur recherché, sinon le premier de la liste triée.
 */
export function resolveSelectedTableauId(
  docs: { sourceTableauId: string }[],
  filter: string | null,
  preferredIds: string[] = []
): string | null {
  if (docs.length === 0) return null;
  if (filter && docs.some((d) => d.sourceTableauId === filter)) {
    return filter;
  }
  for (const id of preferredIds) {
    if (docs.some((d) => d.sourceTableauId === id)) return id;
  }
  return docs[0]?.sourceTableauId ?? null;
}

/** True dès qu’au moins une rencontre de poule a un résultat saisi. */
export function poolsHaveResults(
  pools: { matches: { status: string }[] }[]
): boolean {
  return pools.some((pool) =>
    pool.matches.some((m) =>
      ["TERMINE", "EXEMPT", "FORFAIT", "ABANDON", "EN_COURS"].includes(m.status)
    )
  );
}
